import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { tenderRegionLabel } from '../data/nuts';

// CPV code → human-readable sector label
// Full-code matches first, then 2-digit division fallback
const CPV_EXACT = {
  '72000000': 'IT Services',
  '72200000': 'Software Consulting',
  '72210000': 'Programming Services',
  '72212000': 'Software Development',
  '72220000': 'Systems Consultancy',
  '72230000': 'Custom Software Development',
  '72250000': 'Systems Support',
  '72260000': 'Software Services',
  '72263000': 'Software Implementation',
  '72267000': 'Software Maintenance',
  '72268000': 'Software Supply',
  '48000000': 'Software Packages',
  '80533000': 'Training Services',
  '32000000': 'Communications Equipment',
  '45000000': 'Construction Works',
  '45233000': 'Road Works',
  '79000000': 'Business Services',
  '30000000': 'Office Equipment',
  '98000000': 'Community Services',
};

// COMPLETE CPV 2008 division table (all 45 divisions) — a missing entry here
// leaks raw digit codes into the SECTORS chips (division 77 tenders used to
// show "77310000").
const CPV_DIVISION = {
  '03': 'Agriculture & Farming', '09': 'Petroleum & Fuels', '14': 'Mining & Minerals',
  '15': 'Food & Beverages', '16': 'Agricultural Machinery', '18': 'Clothing & Footwear',
  '19': 'Textiles & Plastics', '22': 'Printed Matter', '24': 'Chemical Products',
  '30': 'Office Equipment', '31': 'Electrical Equipment', '32': 'Communications',
  '33': 'Medical Equipment', '34': 'Transport Equipment', '35': 'Security Equipment',
  '37': 'Sports & Musical Goods', '38': 'Lab Equipment', '39': 'Furniture',
  '41': 'Water', '42': 'Industrial Machinery', '43': 'Construction Machinery',
  '44': 'Construction Materials', '45': 'Construction Works', '48': 'Software',
  '50': 'Repair & Maintenance', '51': 'Installation Services', '55': 'Hospitality',
  '60': 'Transport Services', '63': 'Logistics', '64': 'Postal Services',
  '65': 'Utilities', '66': 'Financial Services', '70': 'Real Estate',
  '71': 'Architecture & Engineering', '72': 'IT Services', '73': 'Research & Development',
  '75': 'Public Administration', '76': 'Oil & Gas Services',
  '77': 'Landscaping & Forestry', '79': 'Business Services', '80': 'Education',
  '85': 'Healthcare & Social', '90': 'Environmental Services',
  '92': 'Cultural & Recreational', '98': 'Community Services',
};

function cpvLabel(code) {
  return CPV_EXACT[code] || CPV_DIVISION[code.slice(0, 2)] || code;
}

function uniqueSectorLabels(codes) {
  const seen = new Set();
  return codes.reduce((acc, code) => {
    const label = cpvLabel(code);
    if (!seen.has(label)) { seen.add(label); acc.push(label); }
    return acc;
  }, []);
}

function formatDate(iso) {
  if (!iso) return 'No deadline';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function formatValue(val, band) {
  if (val) return '€' + val.toLocaleString();
  if (band && band !== 'unknown') return band.charAt(0).toUpperCase() + band.slice(1);
  return 'Unknown value';
}

const VERDICT_CONFIG = {
  great: { background: '#dcfce7', color: '#15803d', label: 'Great fit' },
  good:  { background: '#fef9c3', color: '#854d0e', label: 'Good fit' },
  maybe: { background: '#f1f5f9', color: '#475569', label: 'Maybe' },
  // Promotion flow: this tender was borderline and was individually reviewed
  // before being shown. Blue rather than the green/amber fit scale — it answers
  // "was this checked", not "how good is it". Required, not cosmetic: the
  // component falls back to grey "Maybe" for any verdict it doesn't know.
  promoted: { background: '#eff6ff', color: '#1d4ed8', label: 'Reviewed' },
};

const st = {
  card: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: '20px 24px',
    marginBottom: 16,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 10,
  },
  title: {
    fontWeight: 600,
    fontSize: 16,
    color: '#0f172a',
    lineHeight: 1.4,
    flex: 1,
  },
  scoreBadgeBase: {
    fontWeight: 700,
    fontSize: 14,
    padding: '4px 10px',
    borderRadius: 20,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  meta: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 12,
    fontSize: 13,
    color: '#64748b',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  },
  reasoning: {
    fontSize: 14,
    color: '#374151',
    background: '#f0f7ff',
    padding: '10px 14px',
    borderRadius: 8,
    borderLeft: '3px solid #2563eb',
    marginBottom: 14,
    lineHeight: 1.55,
  },
  sectors: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 14,
    alignItems: 'center',
  },
  sectorLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginRight: 4,
  },
  sectorTag: {
    background: '#f1f5f9',
    color: '#475569',
    fontSize: 12,
    fontWeight: 500,
    padding: '3px 9px',
    borderRadius: 20,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 4,
  },
  tedBtn: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    color: '#374151',
    fontWeight: 500,
    fontSize: 13,
    padding: '7px 14px',
    borderRadius: 8,
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
  },
  reasoningToggle: {
    background: 'none', border: 'none', color: '#2563eb',
    fontSize: 13, cursor: 'pointer', padding: '4px 0 10px', display: 'block',
  },
  bookmarkBtn: (active) => ({
    background: 'none', border: 'none', cursor: 'pointer',
    padding: '4px', color: active ? '#e11d48' : '#cbd5e1',
    display: 'flex', alignItems: 'center', flexShrink: 0,
  }),
  // Both matching lanes agreed on this tender: the semantic score AND the
  // buyer's own CPV classification. Green rather than the blue used for
  // profile/score chips — this is a confidence signal, not metadata.
  doubleMatch: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    background: '#ecfdf5', color: '#047857',
    border: '1px solid #a7f3d0',
    fontSize: 12, fontWeight: 600,
    padding: '2px 9px', borderRadius: 20,
  },
};

export default function TenderCard({ tender, score, verdict, reasoning, source, isBookmarked, onToggleBookmark, profileName, cpvMatch }) {
  const sectors = uniqueSectorLabels(tender.cpv_codes || []);
  const [reasoningExpanded, setReasoningExpanded] = useState(false);

  const vc = verdict ? (VERDICT_CONFIG[verdict] || VERDICT_CONFIG.maybe) : null;
  const numericScore = score ?? 0;
  const isCosineOnly = source === 'cosine' && !verdict;
  const badgeStyle = vc
    ? { ...st.scoreBadgeBase, background: vc.background, color: vc.color }
    : isCosineOnly
      ? { ...st.scoreBadgeBase, background: '#eff6ff', color: '#1d4ed8' }
      : {
          ...st.scoreBadgeBase,
          background: numericScore >= 70 ? '#dcfce7' : numericScore >= 50 ? '#fef9c3' : '#f1f5f9',
          color:      numericScore >= 70 ? '#15803d' : numericScore >= 50 ? '#854d0e' : '#475569',
        };
  const badgeText = vc
    ? `${vc.label} · ${Math.round(numericScore)}`
    : isCosineOnly
      ? `Potential match · ${Math.round(numericScore)}`
      : `${Math.round(numericScore)}/100`;

  return (
    <div style={st.card}>
      <div style={st.header}>
        <Link to={`/tender/${tender.id}`} state={{ score, verdict, reasoning }} style={{ ...st.title, textDecoration: 'none' }}>
          {tender.title || 'Untitled Tender'}
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <button
            style={st.bookmarkBtn(isBookmarked)}
            onClick={() => onToggleBookmark?.(tender.id)}
            title={isBookmarked ? 'Remove bookmark' : 'Save'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill={isBookmarked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </button>
          <div style={badgeStyle}>{badgeText}</div>
        </div>
      </div>

      <div style={st.meta}>
        <span style={st.metaItem}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20"/></svg>
          {(() => {
            const region = tenderRegionLabel(tender);
            const country = tender.country || 'Unknown';
            return region ? `${region} · ${country}` : country;
          })()}
        </span>
        <span style={st.metaItem}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
          {formatDate(tender.deadline)}
        </span>
        <span style={st.metaItem}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
          {formatValue(tender.contract_value, tender.contract_size_band)}
        </span>
        {cpvMatch && (
          <span
            style={st.doubleMatch}
            title={
              cpvMatch.label
                ? `Double match — the buyer filed this tender under "${cpvMatch.label}", a CPV area your profile declares.`
                : 'Double match — this tender matched on wording and on the buyer’s own CPV classification.'
            }
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
            Double match
          </span>
        )}
        {profileName && (
          <span style={{
            ...st.metaItem,
            background: '#eff6ff', color: '#1e40af',
            fontSize: 12, fontWeight: 500,
            padding: '2px 8px', borderRadius: 20,
          }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
            {profileName}
          </span>
        )}
      </div>

      {reasoning && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ ...st.reasoning, marginBottom: 0 }}>
            {reasoningExpanded || reasoning.length <= 120
              ? reasoning
              : reasoning.slice(0, 120) + '…'}
          </div>
          {reasoning.length > 120 && (
            <button style={st.reasoningToggle} onClick={() => setReasoningExpanded((e) => !e)}>
              {reasoningExpanded ? 'Show less ▴' : 'Show reasoning ▾'}
            </button>
          )}
        </div>
      )}

      {sectors.length > 0 && (
        <div style={st.sectors}>
          <span style={st.sectorLabel}>Sectors</span>
          {sectors.map((label) => (
            <span key={label} style={st.sectorTag}>{label}</span>
          ))}
        </div>
      )}

      <div style={st.actions}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Link to={`/tender/${tender.id}`} state={{ score, verdict, reasoning }} style={st.tedBtn}>
            View details →
          </Link>
          {tender.ted_id && (
            <a
              href={`https://ted.europa.eu/en/notice/-/detail/${tender.ted_id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={st.tedBtn}
            >
              View on TED
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
